import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Flame, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Props {
  listingId: string;
  listingType?: 'property' | 'plot' | 'project';
  /** Compact icon-only variant */
  compact?: boolean;
}

interface BoostRow {
  subscription_id: string;
  package_id: string;
  package_name: string;
  tier: string;
  quota: number;
  used: number;
  expires_at: string;
  pages: string[];
}

interface ActiveBoost {
  id: string;
  tier: string;
  end_date: string;
  subscription_id: string | null;
}

export function BoostListingButton({ listingId, listingType = 'property', compact = false }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string>('');

  const { data: boosts } = useQuery({
    queryKey: ['boost-summary', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.rpc('subscription_boost_summary' as any, { _user_id: user!.id });
      return ((data as any[]) || []) as BoostRow[];
    },
  });

  const { data: active } = useQuery({
    queryKey: ['active-boost', listingId],
    queryFn: async () => {
      const { data } = await supabase
        .from('featured_listings' as any)
        .select('id,tier,end_date,subscription_id')
        .eq('listing_id', listingId)
        .eq('listing_type', listingType)
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString().slice(0, 10))
        .maybeSingle();
      return (data as unknown) as ActiveBoost | null;
    },
  });

  const usable = (boosts || []).filter((b) => b.used < b.quota);
  const isBoosted = !!active;

  const apply = async () => {
    if (!chosen) {
      toast.error('Pick a subscription');
      return;
    }
    const { error } = await supabase.rpc('boost_my_listing' as any, {
      _listing_id: listingId,
      _subscription_id: chosen,
      _listing_type: listingType,
    });
    if (error) return toast.error(error.message);
    toast.success('Listing boosted! It will appear higher in search results.');
    setOpen(false);
    qc.invalidateQueries({ queryKey: ['boost-summary', user?.id] });
    qc.invalidateQueries({ queryKey: ['active-boost', listingId] });
    qc.invalidateQueries({ queryKey: ['my-subscription-boosts', user?.id] });
  };

  const remove = async () => {
    if (!active) return;
    const { error } = await supabase.rpc('unboost_my_listing' as any, { _featured_id: active.id });
    if (error) return toast.error(error.message);
    toast.success('Boost removed');
    qc.invalidateQueries({ queryKey: ['boost-summary', user?.id] });
    qc.invalidateQueries({ queryKey: ['active-boost', listingId] });
    qc.invalidateQueries({ queryKey: ['my-subscription-boosts', user?.id] });
  };

  if (isBoosted) {
    return (
      <div className="flex items-center gap-1.5">
        <Badge variant="default" className="gap-1 capitalize">
          <Flame className="h-3 w-3" />
          {active.tier.replace('_', ' ')} until {format(new Date(active.end_date), 'd MMM')}
        </Badge>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={remove}>
          Remove
        </Button>
      </div>
    );
  }

  if (!boosts?.length) {
    return (
      <Button asChild variant="outline" size="sm" className="gap-1">
        <a href="/dashboard/subscription">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          {compact ? '' : 'Boost (Upgrade)'}
        </a>
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={compact ? 'ghost' : 'outline'}
          size="sm"
          className="gap-1"
          disabled={!usable.length}
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          {compact ? '' : 'Boost'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Boost this listing to the top of search</DialogTitle>
          <DialogDescription>
            Pick which subscription's quota to use. Boosted listings rank above organic results
            until your subscription expires.
          </DialogDescription>
        </DialogHeader>

        {!usable.length ? (
          <p className="text-sm text-muted-foreground">
            All your boost slots are in use. Remove a boost from another listing or upgrade your package.
          </p>
        ) : (
          <div className="space-y-2">
            <Select value={chosen} onValueChange={setChosen}>
              <SelectTrigger><SelectValue placeholder="Choose a subscription…" /></SelectTrigger>
              <SelectContent>
                {usable.map((b) => (
                  <SelectItem key={b.subscription_id} value={b.subscription_id}>
                    {b.package_name} — {String(b.tier).replace('_', ' ')} ({b.used}/{b.quota} used)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosen && (() => {
              const b = usable.find((x) => x.subscription_id === chosen)!;
              return (
                <p className="text-xs text-muted-foreground">
                  Boost runs until <strong>{format(new Date(b.expires_at), 'd MMM yyyy')}</strong>{' '}
                  on pages: {b.pages.join(', ')}.
                </p>
              );
            })()}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={apply} disabled={!chosen || !usable.length}>Boost listing</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
